/**
 * Sweet Wink — "Halloween 👻👻" collection detail page.
 *
 * A dedicated page for one of Sweet Wink's real, published Faire collections
 * (token pbc_krhzdcamfc — name/description/hero image are all real, pulled
 * via entity-db's import-collections). The collection's real product
 * membership isn't fetchable (see the framework SKILL.md's swallowed-401
 * note), so `productTokens` on that collection entity is a curated subset of
 * real, individually-imported products (per Ryan) rather than its full 81.
 */
import RetailerLayout from "@faire/prototype-playground/components/RetailerLayout";
import { PADX } from "@faire/prototype-playground/components/shared/brand-home-framework/constants";
import { formatMoney } from "@faire/prototype-playground/components/shared/brand-home-framework/PriceLabel";
import EvaluateProductTile from "@faire/prototype-playground/components/shared/EvaluateProductTile";
import { getEntityByToken } from "@faire/prototype-playground/data/get_entity";
import { cn } from "@faire/prototype-playground/lib/utils";
import React, { useEffect } from "react";

import { SWEET_WINK } from "./data";

const HALLOWEEN_COLLECTION_TOKEN = "pbc_krhzdcamfc";

export const HalloweenCollectionPage: React.FC = () => {
  useEffect(() => {
    document.title = "Halloween — Sweet Wink";
  }, []);

  const collection = getEntityByToken(HALLOWEEN_COLLECTION_TOKEN, "collection");
  const products = (collection?.productTokens ?? [])
    .map((token) => getEntityByToken(token, "product"))
    .filter(
      (product): product is NonNullable<typeof product> => product !== undefined
    );

  return (
    <RetailerLayout
      languageSelector={false}
      cartCount={0}
      signedIn
      searchPlaceholder="Search for products or brands"
    >
      <div className={cn("border-fs-border-muted border-b pt-8 pb-6", PADX)}>
        <div className="flex items-start justify-between gap-6">
          <div className="flex flex-col gap-2">
            <span className="type-fs-label text-fs-text-subdued">
              Collection
            </span>
            <h1 className="type-fs-display-s text-fs-text-primary">
              {collection?.name ?? "Halloween 👻👻"}
            </h1>
            {collection?.description ? (
              <p className="type-fs-paragraph text-fs-text-primary max-w-2xl">
                {collection.description}
              </p>
            ) : null}
          </div>
          <div className="border-fs-border-muted h-16 w-16 flex-shrink-0 overflow-hidden rounded-full border">
            <img
              src={SWEET_WINK.avatarUrl}
              alt={SWEET_WINK.name}
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      </div>

      <div
        className={cn(
          "grid grid-cols-2 gap-x-2 gap-y-6 py-6 md:grid-cols-3 md:gap-x-4 lg:grid-cols-5",
          PADX
        )}
      >
        {products.map((product) => (
          <EvaluateProductTile
            key={product.token}
            id={product.token}
            name={product.name}
            brandName={product.brandName}
            imageUrl={product.imageUrl}
            price={formatMoney(product.priceDollars) ?? "—"}
            msrp={formatMoney(product.msrpDollars)}
            caseSize={product.caseSize}
            minOrder={
              product.caseSize ? `Case of ${product.caseSize}` : undefined
            }
            freeShipping={product.freeShipping}
            isBestseller={product.isBestseller}
          />
        ))}
      </div>
    </RetailerLayout>
  );
};
